<template>
  <div class="store-management">
    <div class="page-header">
      <h2>门店管理</h2>
      <el-button type="primary" @click="showAddDialog">
        <el-icon><Plus /></el-icon> 新增门店
      </el-button>
    </div>

    <!-- Stats Cards -->
    <el-row :gutter="20" class="stats-row">
      <el-col :span="6">
        <el-card class="stats-card" shadow="hover">
          <div class="stats-value">{{ stats.total_stores }}</div>
          <div class="stats-label">门店总数</div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="stats-card online" shadow="hover">
          <div class="stats-value">{{ stats.online_stores }}</div>
          <div class="stats-label">在线门店</div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="stats-card warning" shadow="hover">
          <div class="stats-value">{{ stats.today_orders }}</div>
          <div class="stats-label">今日订单</div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="stats-card success" shadow="hover">
          <div class="stats-value">{{ stats.today_printed }}</div>
          <div class="stats-label">今日打印</div>
        </el-card>
      </el-col>
    </el-row>

    <!-- Store List -->
    <el-card class="store-list">
      <el-table :data="stores" v-loading="loading" stripe>
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="name" label="门店名称" />
        <el-table-column prop="store_key" label="门店密钥" width="280">
          <template #default="{ row }">
            <div class="key-cell">
              <span class="key-text">{{ showKey[row.id] ? row.store_key : maskKey(row.store_key) }}</span>
              <el-button link @click="toggleKey(row.id)">
                <el-icon><View v-if="!showKey[row.id]" /><Hide v-else /></el-icon>
              </el-button>
              <el-button link @click="copyKey(row.store_key)">
                <el-icon><DocumentCopy /></el-icon>
              </el-button>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="address" label="地址" show-overflow-tooltip />
        <el-table-column label="今日统计" width="200">
          <template #default="{ row }">
            <div class="mini-stats">
              <el-tag size="small" type="warning">{{ row.stats?.today_orders || 0 }} 单</el-tag>
              <el-tag size="small" type="success">{{ row.stats?.today_printed || 0 }} 印</el-tag>
              <el-tag size="small" v-if="row.stats?.pending" type="danger">{{ row.stats.pending }} 待</el-tag>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'">
              {{ row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="last_online_at" label="最后在线" width="180">
          <template #default="{ row }">
            <span :class="{ 'online-now': isOnline(row.last_online_at) }">
              {{ row.last_online_at ? formatDate(row.last_online_at) : '从未' }}
              <el-tag v-if="isOnline(row.last_online_at)" size="small" type="success">在线</el-tag>
            </span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="320" fixed="right">
          <template #default="{ row }">
            <el-button link type="primary" @click="editStore(row)">编辑</el-button>
            <el-button link type="info" @click="viewStats(row)">统计</el-button>
            <el-button link type="warning" @click="resetKey(row)">重置密钥</el-button>
            <el-button link @click="managePrinters(row)">打印机</el-button>
            <el-button link type="danger" @click="deleteStore(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- Add/Edit Dialog -->
    <el-dialog v-model="dialogVisible" :title="isEdit ? '编辑门店' : '新增门店'" width="500px">
      <el-form :model="form" label-width="100px">
        <el-form-item label="门店名称" required>
          <el-input v-model="form.name" placeholder="请输入门店名称" />
        </el-form-item>
        <el-form-item v-if="!isEdit" label="门店密钥" required>
          <el-input v-model="form.store_key" placeholder="自动生成或手动输入" />
        </el-form-item>
        <el-form-item label="地址">
          <el-input v-model="form.address" placeholder="请输入地址" />
        </el-form-item>
        <el-form-item label="联系人">
          <el-input v-model="form.contact_name" placeholder="请输入联系人" />
        </el-form-item>
        <el-form-item label="联系电话">
          <el-input v-model="form.contact_phone" placeholder="请输入联系电话" />
        </el-form-item>
        <el-form-item label="状态">
          <el-switch
            v-model="form.status"
            :active-value="1"
            :inactive-value="0"
            active-text="启用"
            inactive-text="禁用"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveStore" :loading="saving">保存</el-button>
      </template>
    </el-dialog>

    <!-- Stats Dialog -->
    <el-dialog v-model="statsDialogVisible" title="门店统计" width="600px">
      <div v-if="currentStats" class="stats-detail">
        <h3>{{ currentStore?.name }}</h3>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-statistic title="今日订单" :value="currentStats.today_orders" />
          </el-col>
          <el-col :span="12">
            <el-statistic title="今日打印" :value="currentStats.today_printed" />
          </el-col>
        </el-row>
        <el-row :gutter="20" class="mt-20">
          <el-col :span="12">
            <el-statistic title="累计打印" :value="currentStats.total_printed" />
          </el-col>
          <el-col :span="12">
            <el-statistic title="待处理" :value="currentStats.pending" />
          </el-col>
        </el-row>
        <div class="chart-section" v-if="currentStats.week_history?.length">
          <h4>近7天打印量</h4>
          <div class="bar-chart">
            <div v-for="item in currentStats.week_history" :key="item.date" class="bar-item">
              <div class="bar" :style="{ height: getBarHeight(item.count) + 'px' }"></div>
              <div class="bar-label">{{ formatShortDate(item.date) }}</div>
              <div class="bar-value">{{ item.count }}</div>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- Printer Dialog -->
    <el-dialog v-model="printerDialogVisible" title="打印机管理" width="600px">
      <div class="printer-header">
        <span>门店: {{ currentStore?.name }}</span>
        <el-button type="primary" size="small" @click="showAddPrinter">
          <el-icon><Plus /></el-icon> 添加打印机
        </el-button>
      </div>
      <el-table :data="printers" v-loading="printerLoading" stripe size="small">
        <el-table-column prop="printer_name" label="打印机名称" />
        <el-table-column prop="printer_type" label="类型" width="120" />
        <el-table-column prop="is_default" label="默认" width="80">
          <template #default="{ row }">
            <el-tag v-if="row.is_default" type="success" size="small">是</el-tag>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100">
          <template #default="{ row }">
            <el-button link type="danger" size="small" @click="deletePrinter(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-dialog>

    <!-- Add Printer Dialog -->
    <el-dialog v-model="addPrinterDialogVisible" title="添加打印机" width="400px">
      <el-form :model="printerForm" label-width="100px">
        <el-form-item label="打印机名称" required>
          <el-input v-model="printerForm.printer_name" placeholder="如: HP LaserJet 1020" />
        </el-form-item>
        <el-form-item label="类型">
          <el-input v-model="printerForm.printer_type" placeholder="如: 激光/喷墨/热敏" />
        </el-form-item>
        <el-form-item label="设为默认">
          <el-switch v-model="printerForm.is_default" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="addPrinterDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="savePrinter" :loading="savingPrinter">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, View, Hide, DocumentCopy } from '@element-plus/icons-vue'
import request from '@/utils/request'

const stores = ref([])
const printers = ref([])
const loading = ref(false)
const printerLoading = ref(false)
const saving = ref(false)
const savingPrinter = ref(false)
const dialogVisible = ref(false)
const statsDialogVisible = ref(false)
const printerDialogVisible = ref(false)
const addPrinterDialogVisible = ref(false)
const isEdit = ref(false)
const currentStore = ref(null)
const currentStats = ref(null)
const showKey = ref({})
const stats = ref({ total_stores: 0, online_stores: 0, today_orders: 0, today_printed: 0 })

const form = ref({
  name: '',
  store_key: '',
  address: '',
  contact_name: '',
  contact_phone: '',
  phone: '',
  status: 1
})

const printerForm = ref({
  printer_name: '',
  printer_type: 'normal',
  is_default: false
})

const maskKey = (key) => {
  if (!key) return ''
  return key.substring(0, 6) + '****' + key.substring(key.length - 4)
}

const toggleKey = (id) => {
  showKey.value[id] = !showKey.value[id]
}

const copyKey = (key) => {
  navigator.clipboard.writeText(key).then(() => {
    ElMessage.success('密钥已复制')
  }).catch(() => {
    const input = document.createElement('input')
    input.value = key
    document.body.appendChild(input)
    input.select()
    document.execCommand('copy')
    document.body.removeChild(input)
    ElMessage.success('密钥已复制')
  })
}

const formatDate = (date) => {
  return new Date(date).toLocaleString('zh-CN')
}

const formatShortDate = (dateStr) => {
  const d = new Date(dateStr)
  return `${d.getMonth() + 1}/${d.getDate()}`
}

const isOnline = (lastOnline) => {
  if (!lastOnline) return false
  const diff = new Date() - new Date(lastOnline)
  return diff < 5 * 60 * 1000 // 5 minutes
}

const getBarHeight = (count) => {
  const max = Math.max(...(currentStats.value?.week_history?.map(i => i.count) || [1]))
  return Math.max(20, (count / max) * 100)
}

const calculateStats = () => {
  const now = new Date()
  stats.value.total_stores = stores.value.length
  stats.value.online_stores = stores.value.filter(s => isOnline(s.last_online_at)).length
  stats.value.today_orders = stores.value.reduce((sum, s) => sum + (s.stats?.today_orders || 0), 0)
  stats.value.today_printed = stores.value.reduce((sum, s) => sum + (s.stats?.today_printed || 0), 0)
}

const fetchStores = async () => {
  loading.value = true
  try {
    const res = await request.get('/api/admin/stores')
    if (res.code === 0) {
      stores.value = res.data || []
      calculateStats()
    }
  } catch (error) {
    ElMessage.error('获取门店列表失败')
  } finally {
    loading.value = false
  }
}

const viewStats = async (row) => {
  currentStore.value = row
  statsDialogVisible.value = true
  currentStats.value = null
  try {
    const res = await request.get(`/api/admin/stores/${row.id}/stats`)
    if (res.code === 0) {
      currentStats.value = res.data
    }
  } catch (error) {
    ElMessage.error('获取统计失败')
  }
}

const generateStoreKey = () => {
  return 'SK' + Math.random().toString(36).substring(2, 10).toUpperCase() +
         Math.random().toString(36).substring(2, 10).toUpperCase()
}

const showAddDialog = () => {
  isEdit.value = false
  form.value = { name: '', store_key: generateStoreKey(), address: '', contact_name: '', contact_phone: '', phone: '', status: 1 }
  dialogVisible.value = true
}

const editStore = (row) => {
  isEdit.value = true
  form.value = { ...row }
  dialogVisible.value = true
}

const saveStore = async () => {
  if (!form.value.name) {
    ElMessage.warning('请输入门店名称')
    return
  }
  if (!isEdit.value && !form.value.store_key) {
    ElMessage.warning('门店密钥不能为空')
    return
  }
  
  saving.value = true
  try {
    const url = isEdit.value ? `/api/admin/stores/${form.value.id}` : '/api/admin/stores'
    const method = isEdit.value ? 'put' : 'post'
    // Map frontend fields to backend expected fields
    const payload = {
      name: form.value.name,
      store_key: form.value.store_key,
      address: form.value.address,
      phone: form.value.contact_phone || form.value.phone
    }
    const res = await request[method](url, payload)
    
    if (res.code === 0) {
      ElMessage.success(isEdit.value ? '更新成功' : '创建成功')
      dialogVisible.value = false
      fetchStores()
    } else {
      ElMessage.error(res.message || '操作失败')
    }
  } catch (error) {
    ElMessage.error('操作失败')
  } finally {
    saving.value = false
  }
}

const deleteStore = (row) => {
  ElMessageBox.confirm(`确定删除门店 "${row.name}" 吗？`, '确认删除', {
    confirmButtonText: '删除',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(async () => {
    try {
      const res = await request.delete(`/api/admin/stores/${row.id}`)
      if (res.code === 0) {
        ElMessage.success('删除成功')
        fetchStores()
      }
    } catch (error) {
      ElMessage.error('删除失败')
    }
  }).catch(() => {})
}

const resetKey = (row) => {
  ElMessageBox.confirm(`确定重置 "${row.name}" 的门店密钥吗？重置后旧密钥将失效。`, '确认重置', {
    confirmButtonText: '重置',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(async () => {
    try {
      const res = await request.post(`/api/admin/stores/${row.id}/reset-key`)
      if (res.code === 0) {
        ElMessage.success(`新密钥: ${res.data.store_key}`)
        fetchStores()
      }
    } catch (error) {
      ElMessage.error('重置失败')
    }
  }).catch(() => {})
}

const managePrinters = async (row) => {
  currentStore.value = row
  printerDialogVisible.value = true
  await fetchPrinters(row.id)
}

const fetchPrinters = async (storeId) => {
  printerLoading.value = true
  try {
    const res = await request.get(`/api/admin/stores/${storeId}/printers`)
    if (res.code === 0) {
      printers.value = res.data || []
    }
  } catch (error) {
    ElMessage.error('获取打印机列表失败')
  } finally {
    printerLoading.value = false
  }
}

const showAddPrinter = () => {
  printerForm.value = { printer_name: '', printer_type: 'normal', is_default: false }
  addPrinterDialogVisible.value = true
}

const savePrinter = async () => {
  if (!printerForm.value.printer_name) {
    ElMessage.warning('请输入打印机名称')
    return
  }
  
  savingPrinter.value = true
  try {
    const res = await request.post(`/api/admin/stores/${currentStore.value.id}/printers`, printerForm.value)
    if (res.code === 0) {
      ElMessage.success('添加成功')
      addPrinterDialogVisible.value = false
      fetchPrinters(currentStore.value.id)
    }
  } catch (error) {
    ElMessage.error('添加失败')
  } finally {
    savingPrinter.value = false
  }
}

const deletePrinter = (row) => {
  ElMessageBox.confirm(`确定删除打印机 "${row.printer_name}" 吗？`, '确认删除', {
    type: 'warning'
  }).then(async () => {
    try {
      const res = await request.delete(`/api/admin/stores/${currentStore.value.id}/printers/${row.id}`)
      if (res.code === 0) {
        ElMessage.success('删除成功')
        fetchPrinters(currentStore.value.id)
      }
    } catch (error) {
      ElMessage.error('删除失败')
    }
  }).catch(() => {})
}

onMounted(() => {
  fetchStores()
})
</script>

<style scoped>
.store-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 24px;
  color: #303133;
}

.stats-row {
  margin-bottom: 20px;
}

.stats-card {
  text-align: center;
  padding: 10px;
}

.stats-card .stats-value {
  font-size: 32px;
  font-weight: bold;
  color: #409EFF;
}

.stats-card.online .stats-value {
  color: #67C23A;
}

.stats-card.warning .stats-value {
  color: #E6A23C;
}

.stats-card.success .stats-value {
  color: #67C23A;
}

.stats-card .stats-label {
  font-size: 14px;
  color: #909399;
  margin-top: 5px;
}

.store-list {
  margin-top: 10px;
}

.key-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.key-text {
  font-family: monospace;
  font-size: 13px;
  color: #606266;
}

.mini-stats {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
}

.online-now {
  color: #67C23A;
  font-weight: 500;
}

.printer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #ebeef5;
}

.stats-detail {
  padding: 10px;
}

.stats-detail h3 {
  margin: 0 0 20px 0;
  text-align: center;
  color: #303133;
}

.mt-20 {
  margin-top: 20px;
}

.chart-section {
  margin-top: 30px;
}

.chart-section h4 {
  margin-bottom: 15px;
  color: #606266;
}

.bar-chart {
  display: flex;
  align-items: flex-end;
  justify-content: space-around;
  height: 150px;
  padding: 20px;
  background: #f5f7fa;
  border-radius: 8px;
}

.bar-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
}

.bar {
  width: 30px;
  background: linear-gradient(to top, #409EFF, #66b1ff);
  border-radius: 4px 4px 0 0;
  transition: height 0.3s;
  min-height: 5px;
}

.bar-label {
  font-size: 12px;
  color: #909399;
  margin-top: 8px;
}

.bar-value {
  font-size: 12px;
  color: #409EFF;
  font-weight: bold;
  margin-top: 4px;
}
</style>
